import { type FC } from 'react';
import { styled } from 'styled-components';

import {
  ID_DATE_FIELDS,
  ID_EDITABLE_FIELDS,
  ID_FIELD_LABELS,
  type IdFieldName,
  type IdFieldValues,
  missingRequiredFields,
} from '@dutchie-pos/requests/checkin/typed-id-entry';

import { DutchieButton } from 'src/components/dutchie-button';
import { DutchieInput } from 'src/components/dutchie-input';

import type { DuplicateGuestMatch } from '../../../queries/curbside/guest-entry';

type CompletionStepProps = {
  isSubmitting: boolean;
  values: IdFieldValues;
  /** A profile the location already has for this person. Replaces the form while it is offered. */
  match: DuplicateGuestMatch | null;
  /**
   * A profile this screen itself already created, whose check-in then failed. Replaces the form
   * the same way `match` does — and for the same reason: once a profile exists, nothing here
   * should be editable, only retryable against the exact id and MJ id that create actually used.
   */
  createdPendingCheckIn: { Guest_id: number; MJStateIDNo: string | null } | null;
  onChange: (field: IdFieldName, value: string) => void;
  onRetake: () => void;
  onSubmit: () => void;
  onCheckInMatch: () => void;
  onRetryCreatedCheckIn: () => void;
};

const matchName = (match: DuplicateGuestMatch): string =>
  match.FullName?.trim() || [match.FirstName, match.LastName].filter(Boolean).join(' ').trim() || 'this customer';

const stepPrompt = (hasMatch: boolean, hasCreatedPendingCheckIn: boolean): string => {
  if (hasMatch) {
    return 'This may already be a customer';
  }

  return hasCreatedPendingCheckIn ? 'Profile created' : 'Enter the ID details';
};

/**
 * Hand entry, reached only when a barcode would not decode — so it starts blank rather than
 * prefilled: a PDF417 yields every field or none, and a partial prefill would be a read nobody made.
 *
 * No photo opt-in. The front-of-ID photo is decided from what the writes answer, for a customer who
 * definitely exists, which is a question this form cannot and should not ask.
 */
export const CompletionStep: FC<CompletionStepProps> = ({
  isSubmitting,
  values,
  match,
  createdPendingCheckIn,
  onChange,
  onRetake,
  onSubmit,
  onCheckInMatch,
  onRetryCreatedCheckIn,
}) => {
  const missing = missingRequiredFields(values);
  // One of three bodies below the header, never more than one at a time: a genuine duplicate, a
  // fixed retry for a profile this screen already created, or the editable form.
  let stage: 'match' | 'createdPendingCheckIn' | 'form' = 'form';
  if (match) {
    stage = 'match';
  } else if (createdPendingCheckIn) {
    stage = 'createdPendingCheckIn';
  }

  return (
    <Container>
      {/* Scanning again leads: the form is here because a barcode would not read, and the next
          card may. */}
      <Header>
        <Prompt>{stepPrompt(Boolean(match), Boolean(createdPendingCheckIn))}</Prompt>
        <RetakeButton
          automationId='check-in_retake'
          ddActionName='Scan again (check in)'
          secondary
          type='button'
          onClick={onRetake}
        >
          <RetakeIcon aria-hidden='true'>↺</RetakeIcon>
          Scan again
        </RetakeButton>
      </Header>

      {/*
       * One profile, one action. The duplicate endpoint answers with a single record, so there is
       * nothing to choose between — and creating a second profile from here is the mistake this
       * check exists to catch, which is why that is not offered at all.
       */}
      {stage === 'match' && match ? (
        <>
          <MatchCard data-testid='check-in_duplicate-match'>
            <MatchName>{matchName(match)}</MatchName>
            <Hint>Check them in instead of creating a second profile.</Hint>
          </MatchCard>
          <DutchieButton
            automationId='check-in_check-in-match'
            cta
            ddActionName='Check in existing customer (check in)'
            disabled={isSubmitting}
            onClick={onCheckInMatch}
          >
            {isSubmitting ? 'Checking in' : `Check in ${matchName(match)}`}
          </DutchieButton>
        </>
      ) : null}
      {stage === 'createdPendingCheckIn' ? (
        <>
          <MatchCard data-testid='check-in_created-pending-checkin'>
            <Hint>The profile was created, but the check-in did not complete. Retry it below.</Hint>
          </MatchCard>
          <DutchieButton
            automationId='check-in_retry-check-in'
            cta
            ddActionName='Retry check-in (check in)'
            disabled={isSubmitting}
            onClick={onRetryCreatedCheckIn}
          >
            {isSubmitting ? 'Checking in' : 'Retry check-in'}
          </DutchieButton>
        </>
      ) : null}
      {stage === 'form' ? (
        <>
          <Hint>Type these from the ID. All four are needed to find an existing customer.</Hint>
          {ID_EDITABLE_FIELDS.map((field) => (
            <Field key={field}>
              <DutchieInput
                automationId={`check-in_field_${field}`}
                containerMargin='0'
                label={ID_FIELD_LABELS[field]}
                type={ID_DATE_FIELDS.includes(field) ? 'date' : 'text'}
                value={values[field] ?? ''}
                onChange={(event) => onChange(field, event.target.value)}
              />
            </Field>
          ))}
          {missing.length > 0 && (
            <Required data-testid='check-in_missing-required'>
              Fill in {missing.map((field) => ID_FIELD_LABELS[field]).join(', ')}.
            </Required>
          )}
          <DutchieButton
            automationId='check-in_submit'
            cta
            ddActionName='Submit ID details (check in)'
            disabled={missing.length > 0 || isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? 'Checking in' : 'Continue'}
          </DutchieButton>
        </>
      ) : null}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
`;

const Prompt = styled.h2`
  color: black;
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
`;

/** Deliberately not full-width and not a CTA: retaking is the alternative, never the default. */
const RetakeButton = styled(DutchieButton)`
  align-items: center;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.8125rem;
  gap: 0.375rem;
  height: 2.25rem;
  padding: 0 0.875rem;
  white-space: nowrap;
`;

const RetakeIcon = styled.span`
  font-size: 1rem;
  line-height: 1;
`;

const Hint = styled.p`
  color: var(--color-gray-70);
  font-size: 0.875rem;
  margin: 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Required = styled.p`
  color: var(--color-red-60);
  font-size: 0.875rem;
  margin: 0;
`;

const MatchCard = styled.div`
  background-color: var(--color-gray-20);
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
`;

const MatchName = styled.span`
  color: black;
  font-size: 1rem;
  font-weight: 600;
`;
